import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { Mail, Phone, MapPin, Send, Loader2, Instagram, Twitter } from "lucide-react";
import { Link } from "react-router-dom";

const ContactPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const subjects = [
    "General Inquiry",
    "Training Question",
    "Massage Booking",
    "Media Services",
    "Team/Group Rates",
    "Partnership Opportunity",
    "Other"
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="contact-page">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-b from-[#111111] to-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-4xl md:text-6xl text-white mb-4">
            GET IN <span className="text-[#E10600]">TOUCH</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Questions? Ready to get started? Reach out and we'll get back to you fast.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* Phone */}
              <Card className="bg-[#111111] border-[#2A2A2A]">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#E10600]/20 flex items-center justify-center">
                      <Phone className="w-6 h-6 text-[#E10600]" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Phone</p>
                      <a href="tel:856-993-4916" className="text-white font-medium hover:text-[#E10600]">(856) 993-4916</a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Email */}
              <Card className="bg-[#111111] border-[#2A2A2A]">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#E10600]/20 flex items-center justify-center">
                      <Mail className="w-6 h-6 text-[#E10600]" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Email</p>
                      <a href="mailto:topnotchhf@gmail.com" className="text-white font-medium hover:text-[#E10600]">topnotchhf@gmail.com</a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Website */}
              <Card className="bg-[#111111] border-[#2A2A2A]">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#E10600]/20 flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-[#E10600]" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Website</p>
                      <p className="text-[#E10600] font-medium">topnotchhf.com</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Social Links */}
              <Card className="bg-[#111111] border-[#2A2A2A]">
                <CardContent className="p-6">
                  <p className="text-gray-400 text-sm mb-4">Follow Us on Instagram</p>
                  <div className="space-y-3">
                    <a href="https://instagram.com/topnotch_sports_" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-400 hover:text-[#E10600] transition-all" data-testid="social-instagram-sports">
                      <div className="w-10 h-10 rounded-full bg-[#2A2A2A] flex items-center justify-center">
                        <Instagram className="w-5 h-5" />
                      </div>
                      <span className="text-sm">@topnotch_sports_</span>
                    </a>
                    <a href="https://instagram.com/topnotch_flix" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-400 hover:text-[#E10600] transition-all" data-testid="social-instagram-flix">
                      <div className="w-10 h-10 rounded-full bg-[#2A2A2A] flex items-center justify-center">
                        <Instagram className="w-5 h-5" />
                      </div>
                      <span className="text-sm">@topnotch_flix</span>
                    </a>
                    <a href="https://instagram.com/topnotch_handf" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-400 hover:text-[#E10600] transition-all" data-testid="social-instagram-handf">
                      <div className="w-10 h-10 rounded-full bg-[#2A2A2A] flex items-center justify-center">
                        <Instagram className="w-5 h-5" />
                      </div>
                      <span className="text-sm">@topnotch_handf</span>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="bg-[#111111] border-[#2A2A2A]">
              <CardContent className="p-6 md:p-8">
                <h2 className="font-display text-2xl text-white mb-6">SEND US A MESSAGE</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-gray-300">Full Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="form-input mt-1"
                        placeholder="Your name"
                        data-testid="contact-name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-gray-300">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="form-input mt-1"
                        placeholder="your@email.com"
                        data-testid="contact-email"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-gray-300">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="form-input mt-1"
                        placeholder="(555) 123-4567"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject" className="text-gray-300">Subject *</Label>
                      <Select value={formData.subject} onValueChange={(value) => setFormData({...formData, subject: value})}>
                        <SelectTrigger className="form-input mt-1" data-testid="contact-subject">
                          <SelectValue placeholder="Select a subject" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a1a] border-[#2A2A2A]">
                          {subjects.map((subject) => (
                            <SelectItem key={subject} value={subject} className="text-white hover:bg-[#2A2A2A]">
                              {subject}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-300">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="form-input mt-1"
                      placeholder="Tell us what you need..."
                      rows={6}
                      data-testid="contact-message"
                      required
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full cta-btn-primary"
                    disabled={submitting}
                    data-testid="contact-submit"
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        SEND MESSAGE
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-gray-500 text-sm mt-6 text-center">
                  Need to book a session? <Link to="/booking" className="text-[#E10600] hover:underline">Go to booking</Link>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
